import { useState, useEffect } from 'react';
import { IconAlertTriangleFilled, IconTrashXFilled, IconCircleCheckFilled } from '@tabler/icons-react';

const FONT = '"Public Sans", sans-serif';

const inputClass =
  'w-full outline-none bg-white/[0.04] focus:bg-white/[0.07] focus:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] placeholder:text-white/30 transition-all duration-200 ease-out';

const CANCEL_REASONS = [
  'El cliente cancela la orden',
  'Error al capturar la orden',
  'Falta de insumos',
  'Pedido duplicado',
  'Demora en la preparación',
  'Otro',
];

export default function ModalCancelarPedido({ isOpen, onClose, onConfirm }) {
  const [authCode, setAuthCode] = useState('');
  const [selectedReason, setSelectedReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAuthCode('');
      setSelectedReason('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canConfirm = authCode.trim() && selectedReason;

  const handleConfirm = () => {
    if (canConfirm) {
      onConfirm(selectedReason);
      setAuthCode('');
      setSelectedReason('');
    }
  };

  const handleClose = () => {
    setAuthCode('');
    setSelectedReason('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={handleClose}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cancelar-pedido-titulo"
        aria-describedby="cancelar-pedido-descripcion"
        className="flex flex-col overflow-hidden w-full"
        style={{
          backgroundColor: '#000',
          borderRadius: '28px',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          maxWidth: '480px',
          maxHeight: 'min(720px, calc(100dvh - 24px))',
          boxShadow: '0 40px 120px rgba(0, 0, 0, 0.8)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-white/[0.16]">
          <h2
            id="cancelar-pedido-titulo"
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Cancelar pedido
          </h2>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            Esta acción no se puede deshacer
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-5 sm:px-6 py-5">
          <div
            className="flex items-center gap-3 rounded-[20px] p-4"
            style={{ backgroundColor: 'rgba(248, 124, 43, 0.08)' }}
          >
            <span
              className="flex items-center justify-center shrink-0 w-10 h-10 rounded-full"
              style={{ backgroundColor: 'rgba(248, 124, 43, 0.14)' }}
            >
              <IconAlertTriangleFilled size={19} color="#f87c2b" />
            </span>
            <span
              id="cancelar-pedido-descripcion"
              className="min-w-0"
              style={{
                fontSize: '15px',
                lineHeight: '21px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'rgba(255, 255, 255, 0.92)',
                fontFamily: FONT,
              }}
            >
              Este pedido será cancelado permanentemente
            </span>
          </div>

          <div className="flex flex-col gap-2.5 mt-5">
            <span
              id="cancelar-pedido-motivo"
              style={{
                fontSize: '13px',
                lineHeight: '18px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.45)',
                fontFamily: FONT,
              }}
            >
              Motivo de cancelación
            </span>
            <div className="flex flex-wrap gap-2" role="group" aria-labelledby="cancelar-pedido-motivo">
              {CANCEL_REASONS.map((reason) => {
                const active = selectedReason === reason;
                return (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => setSelectedReason(reason)}
                    aria-pressed={active}
                    className="flex items-center gap-1.5 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98]"
                    style={{
                      backgroundColor: active ? '#fff' : 'rgba(255, 255, 255, 0.06)',
                      height: '40px',
                      padding: '0 16px',
                      fontSize: '13px',
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      fontFamily: FONT,
                      color: active ? '#000' : 'rgba(255, 255, 255, 0.75)',
                    }}
                  >
                    {active && <IconCircleCheckFilled size={14} color="#000" />}
                    {reason}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-2 mt-5">
            <label
              htmlFor="cancelar-pedido-clave"
              style={{
                fontSize: '13px',
                lineHeight: '18px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.45)',
                fontFamily: FONT,
              }}
            >
              Clave de autorización
            </label>
            <input
              id="cancelar-pedido-clave"
              type="password"
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
              placeholder="Ingresa la clave"
              autoComplete="current-password"
              className={inputClass}
              style={{
                border: 'none',
                borderRadius: '16px',
                height: '48px',
                padding: '0 16px',
                fontSize: '15px',
                fontWeight: 500,
                fontFamily: FONT,
                color: 'rgba(255, 255, 255, 0.95)',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 shrink-0 px-5 sm:px-6 py-4 border-t border-white/[0.16]">
          <button
            type="button"
            onClick={handleClose}
            className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.1]"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: 'rgba(255, 255, 255, 0.9)',
              fontFamily: FONT,
            }}
          >
            Regresar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex items-center justify-center gap-1.5 h-12 px-7 rounded-full border-none touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:brightness-110"
            style={{
              backgroundColor: canConfirm ? '#ff453a' : 'rgba(255, 255, 255, 0.06)',
              fontSize: '15px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: canConfirm ? '#fff' : 'rgba(255, 255, 255, 0.35)',
              fontFamily: FONT,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
            }}
          >
            <IconTrashXFilled size={16} color={canConfirm ? '#fff' : 'rgba(255, 255, 255, 0.35)'} />
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}
